import { ContentType } from '@/@types/common'
import type { Post as PostDataType } from '@/@types/post'
import day from '@/utils/day'
import usePostInViewTracker from '@/utils/hooks/usePostInViewTracker'
import useUniversalNavigate from '@/utils/hooks/useUniversalNavigate'
import { POST_TYPE_TO_ROUTE_MAPPING } from '@/utils/route'
import { css } from '@emotion/react'
import { LocationFilledIcon, StickStickIcon } from '@ruguoapp/jike-fe-icons'
import { Link, useMatchRoute } from '@tanstack/react-router'
import { isEmpty } from 'lodash-es'
import { type ComponentProps, HTMLAttributes, useRef } from 'react'
import Avatar from '../Avatar'
import LockIcon from '../Icons/LockIcon'
import ScreenNames from '../ScreenNames'
import Tag from '../Tag'
import Topic from '../Topic'
import PostActions from './Actions'
import PostContent from './PostContent'
import PostHeader from './PostHeader'
import PostTarget from './PostTarget'

export interface PostCardProps extends HTMLAttributes<HTMLDivElement> {
	mode?: 'simple' | 'full'
	data: PostDataType

	/**
	 * 禁用操作
	 *
	 * @default false
	 */
	noActions?: boolean

	noTopic?: boolean

	/**
	 * 显示对话线
	 *
	 * @default false
	 */
	showThreadLine?: boolean
}

export default function PostCard({
	mode = 'simple',
	data,
	noActions = false,
	showThreadLine = false,
	noTopic = false,
	...props
}: PostCardProps) {
	const { createdAt, topic, type, content, user, pinned, poi } = data
	const navigate = useUniversalNavigate()
	const ref = useRef<HTMLDivElement>(null)
	const matchRoute = useMatchRoute()
	const isPostPage = !!matchRoute({
		to: '/u/$username/$type/$id',
	})

	const routeTypeParam = POST_TYPE_TO_ROUTE_MAPPING[data.type]
	const navigatable =
		!window.getSelection()?.toString().length &&
		[ContentType.POST, ContentType.REPOST].includes(data.type) &&
		!isEmpty(data.user) &&
		routeTypeParam &&
		!isPostPage

	usePostInViewTracker(data, ref)

	return (
		<div
			ref={ref}
			onClick={(e) => {
				if (!navigatable) {
					return
				}

				navigate(
					{
						to: '/u/$username/$type/$id',
						params: {
							username: data.user!.username,
							id: data.id,
							type: routeTypeParam,
						},
					},
					e
				)
			}}
			data-clickable-feedback={navigatable}
			css={css`
				padding: 20px 15px;
			`}
			{...props}
		>
			<div
				css={css`
					display: grid;
					grid-template-columns: auto 1fr;
					gap: 10px;
				`}
			>
				<div
					css={css`
						display: flex;
						flex-direction: column;
					`}
				>
					<Avatar
						data={user}
						css={css`
							margin: 4px 0;
							cursor: pointer;
						`}
					/>
					{showThreadLine && (
						<div
							css={css`
								flex: 1;
								display: flex;
								justify-content: center;
							`}
						>
							<div
								css={css`
									width: 1px;
									height: 100%;
									transform: translateY(10px);
									background-color: var(--tint-separator);
									border-radius: 2px;
								`}
							/>
						</div>
					)}
				</div>
				<div
					css={css`
						overflow: hidden;
					`}
				>
					{user && (
						<PostHeader
							title={
								<div
									css={css`
										display: grid;
										grid-template-columns: auto 1fr;
										grid-gap: 4px;
									`}
								>
									<ScreenNames data={[user]} />
									{type === ContentType.REPOST && isEmpty(content) && (
										<span
											css={css`
												color: var(--tint-secondary);
											`}
										>
											分享了
										</span>
									)}
								</div>
							}
							desc={
								<div
									css={css`
										display: flex;
										align-items: center;
										gap: 6px;
									`}
								>
									{day(createdAt).fromNow()}
									{poi && (
										<div
											css={css`
												display: flex;
												align-items: center;
											`}
										>
											<LocationFilledIcon
												css={css`
													width: 12px;
													height: 12px;
												`}
											/>
											<span>{poi.name}</span>
										</div>
									)}
								</div>
							}
							right={
								<>
									{data.isPrivate && (
										<Tag>
											<LockIcon />
											<span>仅自己可见</span>
										</Tag>
									)}
									{pinned?.personalUpdate && (
										<div
											css={css`
												color: var(--tint-error);
												display: flex;
												align-items: center;
												gap: 4px;
												font-size: 14px;
												font-weight: 500;
											`}
										>
											<StickStickIcon
												css={css`
													width: 16px;
													height: 16px;
												`}
												color="currentColor"
											/>
											置顶
										</div>
									)}
								</>
							}
							css={css`
								margin-bottom: 10px;
							`}
						/>
					)}

					<div
						css={css`
							appearance: none;
							color: inherit;
							text-decoration: none;
							display: flex;
							flex-direction: column;
							gap: 15px;
						`}
					>
						<PostContent data={data} mode={mode} />
						{!noTopic && (
							<Topic
								data={topic}
								as={Link}
								to="/topic/$id/"
								onClick={(e) => e.stopPropagation()}
								params={{ id: topic?.id } as unknown as ComponentProps<typeof Link>['params']}
							/>
						)}
						<PostTarget data={data} />
					</div>
					{!noActions && (
						<PostActions
							data={data}
							css={css`
								margin-top: 15px;
							`}
						/>
					)}
				</div>
			</div>
		</div>
	)
}
